"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import type { MediaLoadStrategy } from "@/components/drops/view/item/content/media/mediaLoadStrategy";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getDropPreviewImageUrl, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { formatMemesQuickVoteLeftThisRoundText, formatMemesQuickVoteUnratedText } from "@/hooks/memesQuickVote.helpers";
import clsx from "clsx";
import type { ReactNode, TouchEventHandler } from "react";
import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import MemesQuickVoteDescription from "./MemesQuickVoteDescription";
import MemesQuickVoteDropHeader from "./MemesQuickVoteDropHeader";
import useMemesQuickVotePreviewSwipe from "./useMemesQuickVotePreviewSwipe";
const MOBILE_SWIPE_CENTER_SLIDE_INDEX = 1;
interface MemesQuickVotePreviewProps {
  readonly drop: ExtendedDrop;
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly leftThisRoundCount: number;
  readonly renderMode: "active" | "preloaded";
  readonly swipeVoteAmount: number | null;
  readonly uncastPower: number | null;
  readonly unratedCount: number;
  readonly votingLabel: string | null;
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
}
interface MemesQuickVoteTouchSurfaceProps {
  readonly onTouchCancel: TouchEventHandler<HTMLDivElement>;
  readonly onTouchEnd: TouchEventHandler<HTMLDivElement>;
  readonly onTouchMove: TouchEventHandler<HTMLDivElement>;
  readonly onTouchStart: TouchEventHandler<HTMLDivElement>;
}
function getQuickVoteArtworkMediaContent({
  artworkMedia,
  hasTouchScreen,
  htmlPreviewImageUrl,
  loadStrategy
}: {
  readonly artworkMedia: ExtendedDrop["parts"][number]["media"][number] | undefined;
  readonly hasTouchScreen: boolean;
  readonly htmlPreviewImageUrl?: string | undefined;
  readonly loadStrategy: MediaLoadStrategy;
}): ReactNode {
  if (!artworkMedia) {
    return null;
  }
  return <DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} disableAutoPlay={hasTouchScreen || loadStrategy === "eager"} disableModal={hasTouchScreen} htmlPreviewImageUrl={htmlPreviewImageUrl} loadStrategy={loadStrategy} />;
}
function MemesQuickVoteMobileSwipeSurface(t0) {
  const $ = _c(7);
  const {
    className,
    touchSurfaceProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      touchAction: "pan-y"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className || $[2] !== touchSurfaceProps.onTouchCancel || $[3] !== touchSurfaceProps.onTouchEnd || $[4] !== touchSurfaceProps.onTouchMove || $[5] !== touchSurfaceProps.onTouchStart) {
    t2 = <div aria-hidden="true" className={className} style={t1} onTouchStart={touchSurfaceProps.onTouchStart} onTouchMove={touchSurfaceProps.onTouchMove} onTouchEnd={touchSurfaceProps.onTouchEnd} onTouchCancel={touchSurfaceProps.onTouchCancel} />;
    $[1] = className;
    $[2] = touchSurfaceProps.onTouchCancel;
    $[3] = touchSurfaceProps.onTouchEnd;
    $[4] = touchSurfaceProps.onTouchMove;
    $[5] = touchSurfaceProps.onTouchStart;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function MemesQuickVoteMobileDetails(t0) {
  const $ = _c(21);
  const {
    allowDescriptionToggle,
    description,
    drop,
    title,
    touchSurfaceProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      touchAction: "pan-y"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== drop) {
    t2 = <MemesQuickVoteDropHeader drop={drop} />;
    $[1] = drop;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = <h2 className="tw-mb-0 tw-text-[1.15rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-white">{title}</h2>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== allowDescriptionToggle || $[6] !== description || $[7] !== drop) {
    t4 = description && <MemesQuickVoteDescription allowToggle={allowDescriptionToggle} key={drop.id} description={description} />;
    $[5] = allowDescriptionToggle;
    $[6] = description;
    $[7] = drop;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="tw-space-y-3">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 tw-pb-5 tw-pt-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden"><div className="tw-flex tw-flex-col tw-gap-4">{t2}{t5}</div></div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6 || $[16] !== touchSurfaceProps.onTouchCancel || $[17] !== touchSurfaceProps.onTouchEnd || $[18] !== touchSurfaceProps.onTouchMove || $[19] !== touchSurfaceProps.onTouchStart) {
    t7 = <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-[#0d0d0e] md:tw-hidden" style={t1} onTouchStart={touchSurfaceProps.onTouchStart} onTouchMove={touchSurfaceProps.onTouchMove} onTouchEnd={touchSurfaceProps.onTouchEnd} onTouchCancel={touchSurfaceProps.onTouchCancel}>{t6}</div>;
    $[15] = t6;
    $[16] = touchSurfaceProps.onTouchCancel;
    $[17] = touchSurfaceProps.onTouchEnd;
    $[18] = touchSurfaceProps.onTouchMove;
    $[19] = touchSurfaceProps.onTouchStart;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function MemesQuickVoteMediaStage(t0) {
  const $ = _c(18);
  const {
    isInteractive,
    isMobile,
    mediaContent,
    swipeHint,
    swipeOffset,
    touchSurfaceProps
  } = t0;
  if (mediaContent === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-h-[45vh] tw-w-full tw-shrink-0 tw-items-center tw-justify-center tw-border-b tw-border-solid tw-border-white/5 tw-bg-black/30 tw-text-sm tw-text-iron-500 md:tw-h-full md:tw-border-0">Preview unavailable</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = isMobile ? "tw-h-[45vh] tw-border-b tw-border-solid tw-border-white/5" : "md:tw-flex md:tw-h-full md:tw-w-full md:tw-items-center md:tw-justify-center md:tw-border-0";
  let t2;
  if ($[1] !== t1) {
    t2 = clsx("tw-relative tw-shrink-0 tw-overflow-hidden tw-bg-black/40", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== mediaContent) {
    t3 = <div className="tw-flex tw-h-full tw-items-center tw-justify-center md:tw-h-full md:tw-w-full">{mediaContent}</div>;
    $[3] = mediaContent;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isInteractive || $[6] !== isMobile || $[7] !== touchSurfaceProps) {
    t4 = isInteractive && isMobile && (["left", "right"] as const).map(side => <MemesQuickVoteMobileSwipeSurface key={side} className={clsx("tw-absolute tw-inset-y-0 tw-z-20 tw-w-12 md:tw-hidden", side === "left" ? "tw-left-0" : "tw-right-0")} touchSurfaceProps={touchSurfaceProps} />);
    $[5] = isInteractive;
    $[6] = isMobile;
    $[7] = touchSurfaceProps;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isInteractive || $[10] !== swipeHint || $[11] !== swipeOffset) {
    t5 = isInteractive && <><div className={clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-4 tw-z-10 tw-flex tw-items-center tw-text-sm tw-font-semibold tw-text-rose-300 tw-transition-opacity md:tw-hidden", swipeOffset < 0 ? "tw-opacity-100" : "tw-opacity-0")}>Skip</div><div className={clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-4 tw-z-10 tw-flex tw-items-center tw-text-right tw-text-sm tw-font-semibold tw-text-primary-300 tw-transition-opacity md:tw-hidden", swipeOffset > 0 ? "tw-opacity-100" : "tw-opacity-0")}>{swipeHint ? `Vote ${swipeHint}` : "Vote"}</div></>;
    $[9] = isInteractive;
    $[10] = swipeHint;
    $[11] = swipeOffset;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <div className={t2}>{t3}{t4}{t5}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function MemesQuickVotePreviewContent(t0) {
  const $ = _c(79);
  const {
    drop,
    isBusy,
    isMobile,
    leftThisRoundCount,
    renderMode,
    swipeVoteAmount,
    uncastPower,
    unratedCount,
    votingLabel,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe
  } = t0;
  const isInteractive = renderMode === "active";
  const loadStrategy = renderMode === "preloaded" ? "eager" : "in-view";
  const title = drop.metadata.find(_temp)?.data_value ?? "Untitled submission";
  const description = drop.metadata.find(_temp2)?.data_value ?? "";
  const artworkMedia = drop.parts.at(0)?.media.at(0);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const isInteractiveHtmlMedia = artworkMedia?.mime_type === "text/html";
  const previewImageUrl = getDropPreviewImageUrl(drop.metadata) ?? undefined;
  const htmlPreviewImageUrl = isInteractiveHtmlMedia && (hasTouchScreen || renderMode === "preloaded") ? previewImageUrl : undefined;
  let t1;
  bb0: {
    if (swipeVoteAmount === null) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== swipeVoteAmount) {
      t2 = formatNumberWithCommas(swipeVoteAmount);
      $[0] = swipeVoteAmount;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = `${t2} ${votingLabel ?? "votes"}`;
  }
  const swipeHint = t1;
  const swipeInstructionText = swipeHint ? `Swipe left to skip, right to vote ${swipeHint}` : null;
  let t2;
  if ($[2] !== isBusy || $[3] !== isMobile || $[4] !== onAdvanceStart || $[5] !== onSkip || $[6] !== onVoteWithSwipe || $[7] !== swipeVoteAmount) {
    t2 = {
      isBusy,
      isMobile,
      onAdvanceStart,
      onSkip,
      onVoteWithSwipe,
      swipeVoteAmount
    };
    $[2] = isBusy;
    $[3] = isMobile;
    $[4] = onAdvanceStart;
    $[5] = onSkip;
    $[6] = onVoteWithSwipe;
    $[7] = swipeVoteAmount;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    previewCardRef,
    canUseSwiperTouchSurface,
    cardTransform,
    cardTransitionDuration,
    handleCardTransitionEnd,
    handleTouchSurfaceCancel,
    handleTouchSurfaceEnd,
    handleTouchSurfaceMove,
    handleTouchSurfaceStart,
    handleSwiperMove,
    handleSwiperTouchEnd,
    swipeOffset
  } = useMemesQuickVotePreviewSwipe(t2);
  const mediaContent = getQuickVoteArtworkMediaContent({
    artworkMedia,
    hasTouchScreen,
    htmlPreviewImageUrl,
    loadStrategy
  });
  let t3;
  if ($[9] !== handleTouchSurfaceCancel) {
    t3 = event => {
      handleTouchSurfaceCancel(event);
    };
    $[9] = handleTouchSurfaceCancel;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== handleTouchSurfaceEnd) {
    t4 = event_0 => {
      handleTouchSurfaceEnd(event_0);
    };
    $[11] = handleTouchSurfaceEnd;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== handleTouchSurfaceMove) {
    t5 = event_1 => {
      handleTouchSurfaceMove(event_1);
    };
    $[13] = handleTouchSurfaceMove;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== handleTouchSurfaceStart) {
    t6 = event_2 => {
      handleTouchSurfaceStart(event_2);
    };
    $[15] = handleTouchSurfaceStart;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = {
      onTouchCancel: t3,
      onTouchEnd: t4,
      onTouchMove: t5,
      onTouchStart: t6
    };
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const mobileTouchSurfaceProps = t7;
  const t8 = renderMode === "active" ? "quick-vote-preview-card" : "quick-vote-preview-card-preloaded";
  const t9 = cardTransform ?? undefined;
  const t10 = isBusy && "tw-pointer-events-none tw-opacity-70";
  let t11;
  if ($[22] !== t10) {
    t11 = clsx("tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-transition-all tw-duration-200 tw-ease-out", t10);
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = isMobile ? "pan-y" : undefined;
  let t13;
  if ($[24] !== cardTransform || $[25] !== cardTransitionDuration || $[26] !== t12) {
    t13 = {
      transform: cardTransform,
      touchAction: t12,
      transitionDuration: cardTransitionDuration
    };
    $[24] = cardTransform;
    $[25] = cardTransitionDuration;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = renderMode === "active" ? "quick-vote-preview-mobile-context" : "quick-vote-preview-mobile-context-preloaded";
  let t15;
  if ($[28] !== isInteractive || $[29] !== isMobile || $[30] !== mediaContent || $[31] !== mobileTouchSurfaceProps || $[32] !== swipeHint || $[33] !== swipeOffset) {
    t15 = <MemesQuickVoteMediaStage isInteractive={isInteractive} isMobile={isMobile} mediaContent={mediaContent} swipeHint={swipeHint} swipeOffset={swipeOffset} touchSurfaceProps={mobileTouchSurfaceProps} />;
    $[28] = isInteractive;
    $[29] = isMobile;
    $[30] = mediaContent;
    $[31] = mobileTouchSurfaceProps;
    $[32] = swipeHint;
    $[33] = swipeOffset;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== description || $[36] !== drop || $[37] !== isInteractive || $[38] !== mobileTouchSurfaceProps || $[39] !== title) {
    t16 = <MemesQuickVoteMobileDetails allowDescriptionToggle={isInteractive} description={description} drop={drop} title={title} touchSurfaceProps={mobileTouchSurfaceProps} />;
    $[35] = description;
    $[36] = drop;
    $[37] = isInteractive;
    $[38] = mobileTouchSurfaceProps;
    $[39] = title;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t14 || $[42] !== t15 || $[43] !== t16) {
    t17 = <div data-testid={t14} className="tw-flex tw-h-full tw-flex-col md:tw-flex md:tw-min-h-0 md:tw-flex-1 md:tw-p-0">{t15}{t16}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== handleCardTransitionEnd || $[46] !== previewCardRef || $[47] !== t11 || $[48] !== t13 || $[49] !== t17 || $[50] !== t8 || $[51] !== t9) {
    t18 = <article ref={previewCardRef} data-testid={t8} data-quick-vote-transform={t9} className={t11} style={t13} onTransitionEnd={handleCardTransitionEnd}>{t17}</article>;
    $[45] = handleCardTransitionEnd;
    $[46] = previewCardRef;
    $[47] = t11;
    $[48] = t13;
    $[49] = t17;
    $[50] = t8;
    $[51] = t9;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const previewCard = t18;
  const t19 = renderMode === "active" ? "quick-vote-preview-status" : "quick-vote-preview-status-preloaded";
  let t20;
  if ($[53] !== isInteractive || $[54] !== swipeInstructionText) {
    t20 = isInteractive && swipeInstructionText && <span className="tw-sr-only">{swipeInstructionText}</span>;
    $[53] = isInteractive;
    $[54] = swipeInstructionText;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== isInteractive || $[57] !== uncastPower || $[58] !== votingLabel) {
    t21 = isInteractive && typeof uncastPower === "number" && <span className="tw-sr-only tw-text-primary-300">{formatNumberWithCommas(uncastPower)} {votingLabel ?? "votes"} left</span>;
    $[56] = isInteractive;
    $[57] = uncastPower;
    $[58] = votingLabel;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== isInteractive || $[61] !== leftThisRoundCount || $[62] !== unratedCount) {
    t22 = isInteractive && <><span className="tw-sr-only">{formatMemesQuickVoteLeftThisRoundText(leftThisRoundCount)}</span><span className="tw-sr-only">{formatMemesQuickVoteUnratedText(unratedCount)}</span></>;
    $[60] = isInteractive;
    $[61] = leftThisRoundCount;
    $[62] = unratedCount;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t19 || $[65] !== t20 || $[66] !== t21 || $[67] !== t22) {
    t23 = <div data-testid={t19} className="tw-sr-only">{t20}{t21}{t22}</div>;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== canUseSwiperTouchSurface || $[70] !== handleSwiperMove || $[71] !== handleSwiperTouchEnd || $[72] !== isBusy || $[73] !== isInteractive || $[74] !== previewCard) {
    t24 = <div className="tw-relative tw-min-h-0 tw-flex-1">{isInteractive && canUseSwiperTouchSurface ? <Swiper initialSlide={MOBILE_SWIPE_CENTER_SLIDE_INDEX} slidesPerView={1} watchOverflow={false} followFinger={false} longSwipes={false} shortSwipes={false} threshold={0} allowTouchMove={!isBusy} resistanceRatio={0} touchStartPreventDefault={false} className="tw-h-full tw-overflow-visible" onTouchMove={handleSwiperMove} onTouchEnd={handleSwiperTouchEnd}><SwiperSlide aria-hidden="true" className="tw-h-full"><div className="tw-h-px" /></SwiperSlide><SwiperSlide className="tw-h-full">{previewCard}</SwiperSlide><SwiperSlide aria-hidden="true" className="tw-h-full"><div className="tw-h-px" /></SwiperSlide></Swiper> : previewCard}</div>;
    $[69] = canUseSwiperTouchSurface;
    $[70] = handleSwiperMove;
    $[71] = handleSwiperTouchEnd;
    $[72] = isBusy;
    $[73] = isInteractive;
    $[74] = previewCard;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== t23 || $[77] !== t24) {
    t25 = <div className="tw-flex tw-h-full tw-flex-col">{t23}{t24}</div>;
    $[76] = t23;
    $[77] = t24;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  return t25;
}
function _temp2(entry_0) {
  return entry_0.data_key === "description";
}
function _temp(entry) {
  return entry.data_key === "title";
}
export default function MemesQuickVotePreview(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <MemesQuickVotePreviewContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}